import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Map, Upload, FileText, MapPin, Navigation, ExternalLink, Trash2, ChevronDown, ChevronUp, Layers, Search, Satellite, Globe, Camera, ImageIcon, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl, Polyline, Polygon, LayerGroup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../contexts/AuthContext';
import { mediaAPI, projectsAPI } from '../lib/api';
import JSZip from 'jszip';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Numbered marker icon factory
const createNumberedIcon = (number, color) => new L.DivIcon({
  className: 'numbered-marker',
  html: `<div style="position:relative;width:30px;height:36px;">
    <div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);color:white;font-size:10px;font-weight:700;font-family:monospace;text-shadow:0 1px 2px rgba(0,0,0,0.5);">${number}</span>
    </div>
  </div>`,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
  popupAnchor: [0, -36],
});

// Numbered photo icon factory
const createPhotoNumberIcon = (number) => new L.DivIcon({
  className: 'photo-numbered-marker',
  html: `<div style="position:relative;width:32px;height:32px;">
    <div style="width:32px;height:32px;border-radius:50%;background:#F59E0B;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;">
      <span style="color:white;font-size:9px;font-weight:700;font-family:monospace;text-shadow:0 1px 2px rgba(0,0,0,0.5);">${number}</span>
    </div>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const LINE_ICON = (n) => createNumberedIcon(n, '#3B82F6');
const POLY_ICON = (n) => createNumberedIcon(n, '#22C55E');

const BRAZIL_CITIES = [
  { name: 'Sao Paulo, SP', lat: -23.5505, lng: -46.6333 },
  { name: 'Rio de Janeiro, RJ', lat: -22.9068, lng: -43.1729 },
  { name: 'Brasilia, DF', lat: -15.7975, lng: -47.8919 },
  { name: 'Salvador, BA', lat: -12.9714, lng: -38.5124 },
  { name: 'Fortaleza, CE', lat: -3.7172, lng: -38.5433 },
  { name: 'Belo Horizonte, MG', lat: -19.9167, lng: -43.9345 },
  { name: 'Manaus, AM', lat: -3.1190, lng: -60.0217 },
  { name: 'Curitiba, PR', lat: -25.4284, lng: -49.2733 },
  { name: 'Recife, PE', lat: -8.0476, lng: -34.8770 },
  { name: 'Porto Alegre, RS', lat: -30.0346, lng: -51.2177 },
  { name: 'Belem, PA', lat: -1.4558, lng: -48.5024 },
  { name: 'Goiania, GO', lat: -16.6869, lng: -49.2648 },
  { name: 'Guarulhos, SP', lat: -23.4543, lng: -46.5337 },
  { name: 'Campinas, SP', lat: -22.9099, lng: -47.0626 },
  { name: 'Sao Luis, MA', lat: -2.5297, lng: -44.2825 },
  { name: 'Maceio, AL', lat: -9.6658, lng: -35.7353 },
  { name: 'Campo Grande, MS', lat: -20.4697, lng: -54.6201 },
  { name: 'Teresina, PI', lat: -5.0892, lng: -42.8019 },
  { name: 'Joao Pessoa, PB', lat: -7.1195, lng: -34.8450 },
  { name: 'Natal, RN', lat: -5.7945, lng: -35.2110 },
  { name: 'Cuiaba, MT', lat: -15.6014, lng: -56.0979 },
  { name: 'Aracaju, SE', lat: -10.9091, lng: -37.0677 },
  { name: 'Porto Velho, RO', lat: -8.7612, lng: -63.9004 },
  { name: 'Florianopolis, SC', lat: -27.5954, lng: -48.5480 },
  { name: 'Vitoria, ES', lat: -20.3155, lng: -40.3128 },
  { name: 'Macapa, AP', lat: 0.0349, lng: -51.0694 },
  { name: 'Boa Vista, RR', lat: 2.8195, lng: -60.6714 },
  { name: 'Rio Branco, AC', lat: -9.9754, lng: -67.8249 },
  { name: 'Palmas, TO', lat: -10.1689, lng: -48.3317 },
];

const FitBounds = ({ placemarks }) => {
  const map = useMap();
  useEffect(() => {
    if (placemarks.length > 0) {
      const bounds = L.latLngBounds(placemarks.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
  }, [placemarks, map]);
  return null;
};

const parseKML = (kmlText) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(kmlText, 'text/xml');
  const parseError = doc.querySelector('parsererror');
  if (parseError) throw new Error('Arquivo KML invalido');

  const placemarks = [];
  const lineStrings = [];
  const polygons = [];
  const pmElements = doc.querySelectorAll('Placemark');
  let fallbackCounter = 0;

  // Extract label from name: "P-99_VAZAMENTO..." → "P-99", "P 01 TEXTO" → "P-01"
  const extractLabel = (name) => {
    // Match patterns: P-99, P99, P 99, P-099, etc.
    const match = name.match(/^(P[\s\-_]?\d+)/i);
    if (match) {
      let raw = match[1].replace(/[\s_]/g, '-').toUpperCase();
      // Normalize: P99 → P-99, P-01 stays P-01
      if (/^P\d/.test(raw)) raw = 'P-' + raw.slice(1);
      return raw;
    }
    return null;
  };

  pmElements.forEach((pm, idx) => {
    const name = pm.querySelector('name')?.textContent?.trim() || `Ponto ${idx + 1}`;
    const description = pm.querySelector('description')?.textContent?.trim() || '';
    const styleUrl = pm.querySelector('styleUrl')?.textContent?.trim() || '';

    const point = pm.querySelector('Point coordinates');
    if (point) {
      const coords = point.textContent.trim().split(',');
      if (coords.length >= 2) {
        fallbackCounter++;
        const label = extractLabel(name) || `P-${String(fallbackCounter).padStart(2, '0')}`;
        placemarks.push({
          id: `pm_${idx}`, name, label, description, styleUrl, type: 'point',
          lng: parseFloat(coords[0]), lat: parseFloat(coords[1]),
          alt: coords[2] ? parseFloat(coords[2]) : 0,
        });
      }
    }

    const lineString = pm.querySelector('LineString coordinates');
    if (lineString) {
      const points = lineString.textContent.trim().split(/\s+/).filter(Boolean);
      const lineCoords = [];
      points.forEach((p, pi) => {
        const coords = p.split(',');
        if (coords.length >= 2) {
          const lat = parseFloat(coords[1]);
          const lng = parseFloat(coords[0]);
          lineCoords.push([lat, lng]);
          fallbackCounter++;
          const label = extractLabel(name) || `P-${String(fallbackCounter).padStart(2, '0')}`;
          placemarks.push({
            id: `pm_${idx}_line_${pi}`, label,
            name: points.length > 1 ? `${name} [${pi + 1}/${points.length}]` : name,
            description, styleUrl, type: 'linestring',
            lng, lat, alt: coords[2] ? parseFloat(coords[2]) : 0,
          });
        }
      });
      if (lineCoords.length > 1) lineStrings.push({ name, coords: lineCoords });
    }

    const polygon = pm.querySelector('Polygon outerBoundaryIs LinearRing coordinates');
    if (polygon) {
      const points = polygon.textContent.trim().split(/\s+/).filter(Boolean);
      const polyCoords = [];
      points.forEach((p, pi) => {
        const coords = p.split(',');
        if (coords.length >= 2) {
          const lat = parseFloat(coords[1]);
          const lng = parseFloat(coords[0]);
          polyCoords.push([lat, lng]);
          fallbackCounter++;
          const label = extractLabel(name) || `P-${String(fallbackCounter).padStart(2, '0')}`;
          placemarks.push({
            id: `pm_${idx}_poly_${pi}`, label,
            name: points.length > 1 ? `${name} [V${pi + 1}]` : name,
            description, styleUrl, type: 'polygon',
            lng, lat, alt: coords[2] ? parseFloat(coords[2]) : 0,
          });
        }
      });
      if (polyCoords.length > 2) polygons.push({ name, coords: polyCoords });
    }
  });

  return { placemarks, lineStrings, polygons };
};

const formatCoord = (val, type) => {
  const abs = Math.abs(val);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = ((minFloat - min) * 60).toFixed(1);
  const dir = type === 'lat' ? (val >= 0 ? 'N' : 'S') : (val >= 0 ? 'E' : 'W');
  return `${deg}\u00b0 ${min}' ${sec}" ${dir}`;
};

const MapPage = () => {
  const { user, token } = useAuth();
  const [placemarks, setPlacemarks] = useState([]);
  const [lineStrings, setLineStrings] = useState([]);
  const [polygonShapes, setPolygonShapes] = useState([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [citySearch, setCitySearch] = useState('');
  const [mapCenter, setMapCenter] = useState([-14.235, -51.925]);
  const [mapZoom, setMapZoom] = useState(4);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fileInputRef = useRef(null);
  const mapContainerRef = useRef(null);

  // Geotagged photos
  const [projects, setProjects] = useState([]);
  const [selectedPhotoProject, setSelectedPhotoProject] = useState('');
  const [geoPhotos, setGeoPhotos] = useState([]);
  const [showPhotos, setShowPhotos] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await projectsAPI.list();
        const vp = user?.role === 'super_admin' ? res.data : res.data.filter(p => p.has_videos);
        setProjects(vp);
        if (vp.length > 0) {
          const userP = vp.find(p => p.id === user?.project_id);
          setSelectedPhotoProject(userP ? userP.id : vp[0].id);
        }
      } catch (e) { console.error(e); }
    };
    fetchProjects();
  }, [user?.project_id, user?.role]);

  useEffect(() => {
    if (!selectedPhotoProject) return;
    const fetchGeoPhotos = async () => {
      try {
        const geoRes = await fetch(`/api/projects/${selectedPhotoProject}/media/geotagged`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (geoRes.ok) {
          const photos = await geoRes.json();
          setGeoPhotos(photos);
        }
      } catch (e) { console.error(e); }
    };
    fetchGeoPhotos();
  }, [selectedPhotoProject, token]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      mapContainerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, [isFullscreen]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const filteredCities = citySearch.length >= 2
    ? BRAZIL_CITIES.filter(c => c.name.toLowerCase().includes(citySearch.toLowerCase())).slice(0, 8)
    : [];

  const processKMLContent = useCallback((kmlText, fName) => {
    try {
      const results = parseKML(kmlText);
      if (results.placemarks.length === 0) {
        toast.error('Nenhum placemark encontrado no arquivo');
        return;
      }
      setPlacemarks(results.placemarks);
      setLineStrings(results.lineStrings);
      setPolygonShapes(results.polygons);
      setFileName(fName);
      toast.success(`${results.placemarks.length} coordenada(s) extraida(s) de ${fName}`);
    } catch (err) {
      toast.error(err.message || 'Erro ao processar arquivo');
    }
  }, []);

  const processFile = useCallback((file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();

    if (ext === 'kml') {
      const reader = new FileReader();
      reader.onload = (e) => processKMLContent(e.target.result, file.name);
      reader.onerror = () => toast.error('Erro ao ler arquivo');
      reader.readAsText(file);
    } else if (ext === 'kmz') {
      // KMZ is a ZIP containing KML
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const zip = await JSZip.loadAsync(e.target.result);
          const kmlFile = Object.keys(zip.files).find(name => name.toLowerCase().endsWith('.kml'));
          if (!kmlFile) {
            toast.error('Nenhum arquivo KML encontrado dentro do KMZ');
            return;
          }
          const kmlText = await zip.files[kmlFile].async('string');
          processKMLContent(kmlText, file.name);
        } catch (err) {
          toast.error('Erro ao processar arquivo KMZ: ' + (err.message || ''));
        }
      };
      reader.onerror = () => toast.error('Erro ao ler arquivo');
      reader.readAsArrayBuffer(file);
    } else {
      toast.error('Selecione um arquivo .KML ou .KMZ');
    }
  }, [processKMLContent]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    processFile(e.dataTransfer?.files?.[0]);
  }, [processFile]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const openGoogleMaps = (lat, lng) => window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  const openWaze = (lat, lng) => window.open(`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`, '_blank');

  const goToCity = (city) => {
    setMapCenter([city.lat, city.lng]);
    setMapZoom(12);
    setCitySearch('');
    toast.info(`Navegando para ${city.name}`);
  };

  const handleClear = () => {
    setPlacemarks([]);
    setLineStrings([]);
    setPolygonShapes([]);
    setFileName('');
    setExpandedId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const CityNavigator = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => { map.setView(center, zoom); }, [center, zoom, map]);
    return null;
  };

  return (
    <div className="space-y-6" data-testid="map-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <Map size={20} className="text-gold" /> MAPA - LEITOR KML/KMZ
        </h2>
        <div className="flex gap-2">
          {placemarks.length > 0 && (
            <Button onClick={handleClear} variant="outline" className="text-gray-500 hover:text-red-500 hover:border-red-300 text-xs" data-testid="clear-kml-btn">
              <Trash2 size={14} className="mr-2" /> LIMPAR
            </Button>
          )}
        </div>
      </div>

      {/* Upload + City Search Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          className={`card-hud p-6 border-2 border-dashed transition-all duration-300 cursor-pointer md:col-span-2 ${
            isDragging ? 'border-gold bg-gold/10 scale-[1.01]' : 'border-gray-300 hover:border-gold/50 hover:bg-gray-50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          data-testid="kml-drop-zone"
        >
          <input ref={fileInputRef} type="file" accept=".kml,.kmz" className="hidden"
            onChange={(e) => { processFile(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="kml-file-input"
          />
          <div className="text-center">
            <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center transition-all ${isDragging ? 'bg-gold/20 scale-110' : 'bg-gray-100'}`}>
              <Upload size={22} className={isDragging ? 'text-gold' : 'text-gray-400'} />
            </div>
            <p className="text-navy font-rajdhani font-semibold text-base uppercase tracking-wider mb-1">
              {isDragging ? 'SOLTE O ARQUIVO AQUI' : 'CARREGAR ARQUIVO KML / KMZ'}
            </p>
            <p className="text-gray-500 text-xs">Arraste e solte ou clique para selecionar (.kml ou .kmz)</p>
            {fileName && (
              <div className="mt-3 inline-flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-lg px-3 py-1.5">
                <FileText size={14} className="text-gold" />
                <span className="text-navy font-medium text-xs">{fileName}</span>
                <span className="text-gold font-mono text-xs">{placemarks.length} pontos</span>
              </div>
            )}
          </div>
        </div>

        <div className="card-hud p-4 space-y-3" data-testid="city-search">
          <div className="flex items-center gap-2 text-gray-500">
            <Globe size={16} className="text-gold" />
            <span className="font-mono text-xs uppercase font-semibold text-navy">Cidades Brasileiras</span>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={citySearch} onChange={(e) => setCitySearch(e.target.value)}
              className="pl-9 h-9 text-sm input-hud" placeholder="Buscar cidade..." data-testid="city-search-input" />
          </div>
          {filteredCities.length > 0 && (
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {filteredCities.map(city => (
                <button key={city.name} onClick={() => goToCity(city)}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-gold/10 hover:text-gold transition-colors flex items-center gap-2"
                  data-testid={`city-${city.name.replace(/[^a-z]/gi, '-').toLowerCase()}`}>
                  <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                  <span className="text-navy font-medium text-xs">{city.name}</span>
                </button>
              ))}
            </div>
          )}
          {citySearch.length >= 2 && filteredCities.length === 0 && (
            <p className="text-gray-400 text-xs text-center py-2">Nenhuma cidade encontrada</p>
          )}
        </div>
      </div>

      {/* Geotagged Photos Control */}
      {projects.length > 0 && (
        <div className="card-hud p-3 flex items-center gap-3 flex-wrap" data-testid="geotag-controls">
          <div className="flex items-center gap-2">
            <Camera size={16} className="text-gold" />
            <span className="text-navy text-xs font-rajdhani font-semibold uppercase tracking-wider">FOTOS GEOTAG</span>
          </div>
          <Select value={selectedPhotoProject} onValueChange={setSelectedPhotoProject}>
            <SelectTrigger className="w-[200px] input-hud text-xs h-8" data-testid="geotag-project-select">
              <SelectValue placeholder="Projeto" />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant={showPhotos ? 'default' : 'outline'} size="sm" className={`text-[10px] h-7 ${showPhotos ? 'bg-gold text-white hover:bg-gold/90' : 'text-gray-500'}`} onClick={() => setShowPhotos(!showPhotos)} data-testid="toggle-photos-btn">
            <ImageIcon size={12} className="mr-1" /> {geoPhotos.length} FOTOS {showPhotos ? 'ON' : 'OFF'}
          </Button>
        </div>
      )}

      {/* Interactive Map with Fullscreen */}
      <div ref={mapContainerRef} className="card-hud overflow-hidden relative" style={{ height: isFullscreen ? '100vh' : '500px' }} data-testid="interactive-map">
        <Button
          onClick={toggleFullscreen}
          className="absolute top-3 right-3 z-[1000] bg-white/90 hover:bg-white text-navy shadow-md border border-gray-200 h-8 w-8 p-0 rounded-lg"
          data-testid="map-fullscreen-btn"
          title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </Button>
        <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%', zIndex: 0 }} zoomControl={true}>
          <CityNavigator center={mapCenter} zoom={mapZoom} />
          {placemarks.length > 0 && <FitBounds placemarks={placemarks} />}

          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Ruas">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satelite">
              <TileLayer attribution='Tiles &copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Hibrido">
              <LayerGroup>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                <TileLayer attribution='Labels &copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" zIndex={10} />
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}" zIndex={11} />
              </LayerGroup>
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* KML/KMZ point markers with P-xx labels */}
          {placemarks.filter(p => p.type === 'point').map((pm) => (
            <Marker key={pm.id} position={[pm.lat, pm.lng]} icon={createNumberedIcon(pm.label, '#EF4444')}>
              <Popup>
                <div className="text-xs">
                  <p style={{fontWeight:'bold',fontSize:'12px',marginBottom:'4px'}}>{pm.label} - {pm.name}</p>
                  {pm.description && <p style={{color:'#666',marginBottom:'4px'}} dangerouslySetInnerHTML={{ __html: pm.description }} />}
                  <p style={{fontFamily:'monospace',fontSize:'10px',color:'#888'}}>{pm.lat.toFixed(6)}, {pm.lng.toFixed(6)}</p>
                  {pm.alt > 0 && <p style={{fontFamily:'monospace',fontSize:'10px',color:'#888'}}>Alt: {pm.alt.toFixed(1)}m</p>}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Linestring markers (first/last) */}
          {placemarks.filter(p => p.type === 'linestring').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((pm) => (
            <Marker key={pm.id} position={[pm.lat, pm.lng]} icon={LINE_ICON(pm.label)}>
              <Popup>
                <div className="text-xs">
                  <p style={{fontWeight:'bold',fontSize:'12px',marginBottom:'2px'}}>{pm.label} - {pm.name}</p>
                  <p style={{fontFamily:'monospace',fontSize:'10px'}}>{pm.lat.toFixed(6)}, {pm.lng.toFixed(6)}</p>
                </div>
              </Popup>
            </Marker>
          ))}

          {lineStrings.map((ls, idx) => (
            <Polyline key={`line-${idx}`} positions={ls.coords} pathOptions={{ color: '#3B82F6', weight: 3, opacity: 0.8 }} />
          ))}

          {polygonShapes.map((pg, idx) => (
            <Polygon key={`poly-${idx}`} positions={pg.coords} pathOptions={{ color: '#22C55E', fillColor: '#22C55E', fillOpacity: 0.2, weight: 2 }} />
          ))}

          {/* Geotagged photos - use original label from filename */}
          {showPhotos && geoPhotos.map((photo, idx) => {
            const fnameMatch = photo.original_filename.match(/^(P[\s\-_]?\d+)/i);
            let photoLabel;
            if (fnameMatch) {
              photoLabel = fnameMatch[1].replace(/[\s_]/g, '-').toUpperCase();
              if (/^P\d/.test(photoLabel)) photoLabel = 'P-' + photoLabel.slice(1);
            } else {
              photoLabel = `P-${String(idx + 1).padStart(2, '0')}`;
            }
            return (
            <Marker key={`geo-${photo.id}`} position={[photo.gps.lat, photo.gps.lng]} icon={createPhotoNumberIcon(photoLabel)}>
              <Popup maxWidth={280}>
                <div className="text-xs" data-testid={`photo-popup-${photo.id}`}>
                  <img
                    src={`/api/media/${photo.id}/thumbnail?auth=${token}`}
                    alt={photo.original_filename}
                    style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '6px', marginBottom: '6px' }}
                  />
                  <p style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '2px' }}>{photoLabel} - {photo.original_filename}</p>
                  <p style={{ fontFamily: 'monospace', fontSize: '9px', color: '#888' }}>
                    {photo.gps.lat.toFixed(6)}, {photo.gps.lng.toFixed(6)}
                    {photo.gps.altitude != null && ` | Alt: ${photo.gps.altitude}m`}
                  </p>
                  <p style={{ fontFamily: 'monospace', fontSize: '9px', color: '#888' }}>
                    {photo.uploaded_by} | {new Date(photo.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </Popup>
            </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Stats + Table */}
      {placemarks.length > 0 && (
        <div className="space-y-4 animate-slide-up">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card-hud p-4">
              <p className="text-gray-500 text-xs font-mono uppercase">Total de Pontos</p>
              <p className="text-2xl font-rajdhani font-bold text-navy">{placemarks.length}</p>
            </div>
            <div className="card-hud p-4">
              <p className="text-gray-500 text-xs font-mono uppercase">Tipo Ponto</p>
              <p className="text-2xl font-rajdhani font-bold text-red-500">{placemarks.filter(p => p.type === 'point').length}</p>
            </div>
            <div className="card-hud p-4">
              <p className="text-gray-500 text-xs font-mono uppercase">Tipo Linha</p>
              <p className="text-2xl font-rajdhani font-bold text-blue-500">{placemarks.filter(p => p.type === 'linestring').length}</p>
            </div>
            <div className="card-hud p-4">
              <p className="text-gray-500 text-xs font-mono uppercase">Tipo Poligono</p>
              <p className="text-2xl font-rajdhani font-bold text-green-500">{placemarks.filter(p => p.type === 'polygon').length}</p>
            </div>
          </div>

          {(() => {
            const pointsOnly = placemarks
              .filter(p => p.type === 'point')
              .sort((a, b) => {
                const extractNum = (name) => {
                  const match = name.match(/p-\s*([\d\s]+)/i);
                  if (!match) return Infinity;
                  return parseInt(match[1].replace(/\s/g, ''), 10);
                };
                return extractNum(a.name) - extractNum(b.name);
              });
            return pointsOnly.length > 0 ? (
              <div className="card-hud overflow-hidden" data-testid="coordinates-table">
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider flex items-center gap-2">
                    <MapPin size={18} className="text-gold" /> COORDENADAS EXTRAIDAS
                  </h3>
                  <span className="text-gray-500 text-xs font-mono">{pointsOnly.length} ponto(s)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase w-16">Ponto</th>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase" data-testid="header-ponto-descricao">Nome / Descricao</th>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Latitude</th>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Longitude</th>
                        <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Altitude</th>
                        <th className="text-right p-3 text-gray-500 font-mono text-xs uppercase">Abrir no Mapa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pointsOnly.map((pm, idx) => (
                        <tr key={pm.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors group" data-testid={`placemark-row-${idx}`}>
                          <td className="p-3 text-gold font-mono text-sm font-bold" data-testid={`point-label-${idx}`}>{pm.label}</td>
                          <td className="p-3">
                            <div className="max-w-[250px]">
                              <p className="text-navy font-medium text-sm truncate" title={pm.name}>{pm.name}</p>
                              {pm.description && (
                                <button onClick={() => setExpandedId(expandedId === pm.id ? null : pm.id)}
                                  className="flex items-center gap-1 text-gray-400 text-xs hover:text-gold transition-colors mt-0.5">
                                  {expandedId === pm.id ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                                  {expandedId === pm.id ? 'Ocultar' : 'Ver descricao'}
                                </button>
                              )}
                              {expandedId === pm.id && pm.description && (
                                <p className="text-gray-500 text-xs mt-1 whitespace-pre-wrap bg-gray-50 p-2 rounded border border-gray-100" dangerouslySetInnerHTML={{ __html: pm.description }} />
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-gray-600 font-mono text-xs">
                            <div>{pm.lat.toFixed(6)}</div>
                            <div className="text-gray-400 text-[10px]">{formatCoord(pm.lat, 'lat')}</div>
                          </td>
                          <td className="p-3 text-gray-600 font-mono text-xs">
                            <div>{pm.lng.toFixed(6)}</div>
                            <div className="text-gray-400 text-[10px]">{formatCoord(pm.lng, 'lng')}</div>
                          </td>
                          <td className="p-3 text-gray-500 font-mono text-xs">{pm.alt > 0 ? `${pm.alt.toFixed(1)}m` : '-'}</td>
                          <td className="p-3">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" className="h-7 px-2 bg-gold/10 border border-gold/30 text-gold hover:bg-gold hover:text-white text-[10px] rounded-md"
                                onClick={() => { setMapCenter([pm.lat, pm.lng]); setMapZoom(18); }}
                                title="Focar no mapa" data-testid={`focus-map-${idx}`}>
                                <Navigation size={12} className="mr-1" /> Focar
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 text-[10px]"
                                onClick={() => openGoogleMaps(pm.lat, pm.lng)} title="Google Maps">
                                <ExternalLink size={10} className="mr-1" /> Google
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 text-[10px]"
                                onClick={() => openWaze(pm.lat, pm.lng)} title="Waze">
                                <ExternalLink size={10} className="mr-1" /> Waze
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="card-hud p-8 text-center">
                <MapPin size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 font-mono text-sm">Nenhum ponto encontrado no arquivo</p>
              </div>
            );
          })()}
        </div>
      )}

      {placemarks.length === 0 && !fileName && (
        <div className="card-hud p-8 text-center">
          <Map size={48} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 font-mono text-sm">Carregue um arquivo KML ou KMZ para visualizar as coordenadas no mapa</p>
          <p className="text-gray-600 text-xs mt-2">Suporte a placemarks, linhas e poligonos com camada de satelite</p>
        </div>
      )}
    </div>
  );
};

export default MapPage;
